import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type GallerySelectionTypes, useGallerySelection } from '@/context/GallerySelectionContext';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { type AssetIdNameTypes } from '@/types/asset';
import { DAMNodeType, NodeType } from '@/types/tree';
import { isAsset } from '@/lib/utils';
import { useRouter } from 'next/router';

export interface TreeContextProps {
  firstTimeLoaded: boolean;
  setFirstTimeLoaded: Dispatch<SetStateAction<boolean>>;
  sidebarTreeOpenIds: Record<string, boolean>;
  setSidebarTreeOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
  reusableTreeOpenIds: Record<string, boolean>;
  setReusableTreeOpenIds: Dispatch<SetStateAction<Record<string, boolean>>>;
  updatedIds: Array<string>;
  setUpdatedIds: Dispatch<SetStateAction<Array<string>>>;
  selectedNodes: Array<AssetIdNameTypes>;
  setSelectedNodes: Dispatch<SetStateAction<Array<AssetIdNameTypes>>>;
  toggleNodeSelection: (params: GallerySelectionTypes) => void;
  // TODO: Added 'string' to currentSelectedType because of the trash page
  // and its data structure containing 'image' and 'video' asset types
  currentSelectedType: NodeType | string | undefined;
  setCurrentSelectedType: Dispatch<SetStateAction<NodeType | string | undefined>>;
  contextNodeSelection: ({ id, name, type }: AssetIdNameTypes) => void;
  includeSubFolderAssets: boolean;
  toggleSubFolders: () => void;
  removeNodeIds: (nodeIds: Array<string>) => void;
}

export const TreeContext = createContext<TreeContextProps>({
  firstTimeLoaded: true,
  setFirstTimeLoaded: () => {},
  sidebarTreeOpenIds: {},
  setSidebarTreeOpenIds: () => {},
  reusableTreeOpenIds: {},
  setReusableTreeOpenIds: () => {},
  updatedIds: [],
  setUpdatedIds: () => {},
  selectedNodes: [],
  setSelectedNodes: () => {},
  toggleNodeSelection: () => {},
  currentSelectedType: undefined,
  setCurrentSelectedType: () => {},
  contextNodeSelection: () => {},
  includeSubFolderAssets: false,
  toggleSubFolders: () => {},
  removeNodeIds: () => {},
});

export function TreeContextProvider({ children }: PropsWithChildren) {
  const { query, pathname, replace } = useRouter();
  // Selector, not the whole store: a bare subscription re-renders this provider and the tree below it.
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const { deselectAllItems } = useGallerySelection();

  const [sidebarTreeOpenIds, setSidebarTreeOpenIds] = useState<Record<string, boolean>>({});
  const [reusableTreeOpenIds, setReusableTreeOpenIds] = useState<Record<string, boolean>>({});
  const [firstTimeLoaded, setFirstTimeLoaded] = useState(true);
  const [updatedIds, setUpdatedIds] = useState<Array<string>>([]);

  /**
   * TEMPORARY DOUBLE-WRITE — a follow-up PR removes this store.
   *
   * This is a second, older selection that lives alongside the gallery selection in
   * GallerySelectionContext, and callers currently write both on every selection change (see
   * useTableSelection, gallery-layout's select-all, notifications, useDamNavigation). It exists
   * because delete-node-alert-component reads it to bulk-delete assets and nodes together.
   *
   * DM-2517 deliberately left it alone: unifying the two selections is a separate change, and
   * the mixed bulk-delete must keep working untouched until then. Do not build new consumers on
   * it — read `selection` from GallerySelectionContext instead.
   */
  const [selectedNodes, setSelectedNodes] = useState<Array<AssetIdNameTypes>>([]);
  const [currentSelectedType, setCurrentSelectedType] = useState<NodeType | string | undefined>(
    query.folder ? NodeType.Folders : query.album ? NodeType.Albums : NodeType.Assets,
  );
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  // Setting the folder as a selected type for the initial load
  useEffect(() => {
    setCurrentSelectedType(NodeType.Folders);
  }, []);

  /**
   * Persisted in `useUserSettingsStore`; still exposed on this context so its ~17 consumers keep
   * reading it from `useTreeContext()` unchanged. Migrating them to selectors is DM-1736's work.
   */
  const storedIncludeSubFolderAssets = useUserSettingsStore((state) => state.includeSubFolderAssets);
  const setIncludeSubFolderAssets = useUserSettingsStore((state) => state.setIncludeSubFolderAssets);

  /**
   * The URL query outranks the persisted value on load, but only for display — it must not
   * overwrite the saved preference, or opening a shared browse link would silently rewrite the
   * recipient's own setting for every future session. Derived on every render rather than seeded
   * into state, so nothing writes during render.
   */
  const queryIncludeSubFolderAssets =
    query.showSubFolderAssets === undefined ? undefined : query.showSubFolderAssets === 'true';
  const includeSubFolderAssets = queryIncludeSubFolderAssets ?? storedIncludeSubFolderAssets;

  const toggleSubFolders = useCallback(() => {
    if (currentSelectedType !== NodeType.Albums) {
      setIsAllSelected(false);
      setCurrentSelectedType(NodeType.Folders);
      deselectAllItems();
      setIncludeSubFolderAssets(!includeSubFolderAssets);
      const newQuery = {
        ...query,
        showSubFolderAssets: !includeSubFolderAssets,
      };

      void replace(
        {
          pathname: pathname === '/' ? '/browse' : pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [
    currentSelectedType,
    includeSubFolderAssets,
    query,
    pathname,
    replace,
    setIsAllSelected,
    deselectAllItems,
    setIncludeSubFolderAssets,
  ]);

  const contextNodeSelection = useCallback(
    ({ id, name, type }: AssetIdNameTypes) => {
      if (!selectedNodes.some(({ id: nodeId }) => nodeId === id)) {
        setSelectedNodes([{ id, name, type }]);
      }
    },
    [selectedNodes],
  );

  const removeNodeIds = useCallback((nodeIds: Array<string>) => {
    setSidebarTreeOpenIds((prev) => {
      const newOpenIds = { ...prev };
      nodeIds.forEach((id) => delete newOpenIds[id]);
      return newOpenIds;
    });
    setReusableTreeOpenIds((prev) => {
      const newOpenIds = { ...prev };
      nodeIds.forEach((id) => delete newOpenIds[id]);
      return newOpenIds;
    });
    setUpdatedIds((prev) => prev.filter((id) => !nodeIds.includes(id)));
  }, []);

  const toggleNodeSelection = useCallback(
    ({ asset, assets, index, metaKey, shiftKey, ctrlKey }: GallerySelectionTypes) => {
      let newSelectedNodes: Array<AssetIdNameTypes>;
      const { id, name, type } = asset;

      // Plain click, select only this one
      if (!metaKey && !shiftKey && !ctrlKey) {
        if (selectedNodes.length === 1 && selectedNodes[0].id === id) {
          newSelectedNodes = [];
        } else {
          newSelectedNodes = [{ id, name, type }];
        }

        // Shift+click, select range from last to this. Shift click should only work if there is at least one other asset selected.
      } else if (shiftKey && lastSelectedIndex >= 0 && selectedNodes.length !== 0) {
        const [start, end] = lastSelectedIndex < index ? [lastSelectedIndex, index] : [index, lastSelectedIndex];

        const range = assets.slice(start, end + 1).map((a) => ({
          id: a.nodeId,
          name: a.name,
          type: isAsset(a.type) ? DAMNodeType.Asset : a.type,
        }));

        // Merge with base, dedupe by id
        newSelectedNodes = Array.from(new Map([...selectedNodes, ...range].map((n) => [n.id, n])).values());

        // Ctrl/Cmd+click, toggle this one in/out
      } else if (metaKey || ctrlKey) {
        const exists = selectedNodes.some((f) => f.id === id);
        newSelectedNodes = exists ? selectedNodes.filter((n) => n.id !== id) : [...selectedNodes, { id, name, type }];

        // Fallback, same as plain click
      } else {
        newSelectedNodes = [{ id, name, type }];
      }

      // Finally, only keep nodes that still appear in `assets`

      const finalList = assets
        .filter((a) => newSelectedNodes.some((n) => n.id === a.nodeId))
        .map((a) => ({ id: a.nodeId, name: a.name, type: a.type }));

      setSelectedNodes(finalList);

      const wasDeselected = !finalList.some((n) => n.id === id);
      if (wasDeselected) {
        if (finalList.length === 0) {
          setLastSelectedIndex(-1);
        } else {
          const highestIndex = finalList.reduce((max, n) => {
            const idx = assets.findIndex((a) => a.nodeId === n.id);
            return idx > max ? idx : max;
          }, -1);
          setLastSelectedIndex(highestIndex);
        }
      } else {
        setLastSelectedIndex(index);
      }
    },
    [selectedNodes, lastSelectedIndex],
  );

  // A fresh object literal here would invalidate the context on every render of this provider.
  const value = useMemo(
    () => ({
      firstTimeLoaded,
      setFirstTimeLoaded,
      sidebarTreeOpenIds,
      setSidebarTreeOpenIds,
      reusableTreeOpenIds,
      setReusableTreeOpenIds,
      selectedNodes,
      setSelectedNodes,
      toggleNodeSelection,
      currentSelectedType,
      setCurrentSelectedType,
      contextNodeSelection,
      updatedIds,
      setUpdatedIds,
      includeSubFolderAssets,
      toggleSubFolders,
      removeNodeIds,
    }),
    [
      firstTimeLoaded,
      sidebarTreeOpenIds,
      reusableTreeOpenIds,
      selectedNodes,
      toggleNodeSelection,
      currentSelectedType,
      contextNodeSelection,
      updatedIds,
      includeSubFolderAssets,
      toggleSubFolders,
      removeNodeIds,
    ],
  );

  return <TreeContext.Provider value={value}>{children}</TreeContext.Provider>;
}

export function useTreeContext() {
  return useContext(TreeContext);
}
